import ServiceNoticeBanner from '@/components/service-notices/ServiceNoticeBanner.jsx';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useUiStore } from '@/state/uiStore.js';
import { useAuthStore } from '@/state/authStore.js';
import { useFeaturePreviewStore } from '@/state/featurePreviewStore.js';
import ErrorBoundary from '../common/ErrorBoundary.jsx';
import BusyOverlay from '../ui/BusyOverlay.jsx';
import { SIDEBAR_TABS } from './Sidebar.jsx';

/**
 * Lazy-loaded tab module registry.
 *
 * Each entry returns a Promise<{ default: ReactComponent }>. The TabHost
 * lazy-mounts a tab the first time it becomes active and keeps it
 * mounted thereafter (just hides it), matching the monolith's behaviour
 * where `switchTab` runs the init function only on first visit and the
 * tab's DOM persists across switches.
 *
 * Phase 1 ships placeholder modules for every tab. Phase 2+ replace
 * each entry with the real tab module.
 */
const TAB_MODULES = /** @type {const} */ ({
  'dashboard': () => import('@/tabs/Dashboard/index.jsx'),
  'user-management-v2': () => import('@/tabs/UserManagement/index.jsx'),
  'group-management': () => import('@/tabs/GroupManagement/index.jsx'),
  'shares': () => import('@/tabs/Shares/index.jsx'),
  'admin': () => import('@/tabs/Admin/index.jsx'),
  'shared-mailboxes': () => import('@/tabs/SharedMailboxes/index.jsx'),
  'printer-management': () => import('@/tabs/Printers/index.jsx'),
  'policy-finder': () => import('@/tabs/PolicyFinder/index.jsx'),
  'reporting': () => import('@/tabs/Reporting/index.jsx'),
  'asset-management': () => import('@/tabs/Assets/index.jsx'),
  // 'log-analytics' retired as a standalone tab — folded into Reporting → Advanced.
  'security-dashboard': () => import('@/tabs/Security/index.jsx'),
  'tools': () => import('@/tabs/Tools/index.jsx'),
  'network-dashboard': () => import('@/tabs/Network/index.jsx'),
  'sharepoint-management': () => import('@/tabs/SharePoint/index.jsx'),
  'cost-analysis': () => import('@/tabs/CostAnalysis/index.jsx'),
  'automations': () => import('@/tabs/Automations/index.jsx'),
  'claude-usage': () => import('@/tabs/ClaudeUsage/index.jsx'),
  'compliance': () => import('@/tabs/Compliance/index.jsx'),
  'backups': () => import('@/tabs/Backups/index.jsx'),
  'apps': () => import('@/tabs/Apps/index.jsx')
});

const TAB_LABELS = Object.fromEntries(SIDEBAR_TABS.map((t) => [t.tabId, t.label]));

/** @type {Map<string, ReturnType<typeof lazy>>} */
const moduleCache = new Map();

/**
 * Return (and cache) the React.lazy component for a tab id.
 *
 * @param {keyof typeof TAB_MODULES} tabId
 */
function getLazyTab(tabId) {
  const cached = moduleCache.get(tabId);
  if (cached) return cached;
  const importer = TAB_MODULES[tabId];
  if (!importer) return null;
  const Lazy = lazy(importer);
  moduleCache.set(tabId, Lazy);
  return Lazy;
}

/**
 * TabHost — owns the main content area and renders the active tab.
 *
 * Keeps previously-visited tabs mounted (hidden) so state survives
 * sidebar switches, matching the monolith.
 */
export default function TabHost() {
  const activeTab = useUiStore((s) => s.activeTab);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const setActiveTab = useUiStore((s) => s.setActiveTab);
  const previews = useFeaturePreviewStore((s) => s.enabled);
  const previewsLoaded = useFeaturePreviewStore((s) => s.loaded);

  /** @type {[Set<string>, React.Dispatch<React.SetStateAction<Set<string>>>]} */
  const [visited, setVisited] = useState(() => new Set([activeTab]));

  // Track visits so already-loaded tabs stay mounted but hidden.
  useEffect(() => {
    setVisited((prev) => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  // If the active tab is one the user no longer has permission for
  // (e.g. permissions changed), fall back to Dashboard.
  useEffect(() => {
    const meta = SIDEBAR_TABS.find((t) => t.tabId === activeTab);
    if (!meta) return;
    if (meta.adminOnly && !isAdmin) {
      setActiveTab('dashboard');
      return;
    }
    if (meta.permission && !hasPermission(meta.permission)) {
      setActiveTab('dashboard');
      return;
    }
    // A preview tab is only reachable by people on its list (no admin bypass).
    // Wait for the list before bouncing, or a deep link would lose its tab.
    if (meta.preview && previewsLoaded && !previews.includes(meta.preview)) {
      setActiveTab('dashboard');
    }
  }, [activeTab, hasPermission, isAdmin, setActiveTab, previews, previewsLoaded]);

  return (
    <main className="main-content" id="mainContent">
      <div className="container">
        <ServiceNoticeBanner />
        {Array.from(visited).map((tabId) => {
          const Lazy = getLazyTab(/** @type {any} */ (tabId));
          if (!Lazy) return null;
          const isActive = tabId === activeTab;
          return (
            <div
              key={tabId}
              id={`${tabId}-tab`}
              className={`tab-content${isActive ? '' : ' hidden'}`}
            >
              <ErrorBoundary label={TAB_LABELS[tabId] ?? tabId}>
                <Suspense fallback={<BusyOverlay label={`Loading ${TAB_LABELS[tabId] ?? 'tab'}…`} />}>
                  <Lazy />
                </Suspense>
              </ErrorBoundary>
            </div>
          );
        })}
      </div>
    </main>
  );
}
